import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { downloadResource } from "../lib/download";
import DocViewer from "../components/DocViewer";
import VideoModal from "../components/VideoModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { toast } from "sonner";
import { Layers, Download, ChevronDown, CheckCircle2, Circle, FileText, Eye, Youtube, Lock } from "lucide-react";

const STAGE_STATUS_COLOR = { "Not Started": "#9CA3AF", "In Progress": "#3B82F6", "Complete": "#10B981" };
const isPpt = (name) => /\.(pptx?|key|odp)$/i.test(name || "");

const confirmMessage = (c) =>
  c.next ? `Have you completed this task for ${c.site.name}?` : `Mark ${c.site.name} as not completed?`;

function SiteList({ task, onToggle, readOnly }) {
  const sites = task.sites || [];
  return (
    <div className="space-y-1.5">
      {sites.length === 0 && <p className="text-sm text-[#9CA3AF]">No sites attached.</p>}
      {sites.map((s) => readOnly ? (
        <div key={s.name} data-testid={`site-ro-${task.id}-${s.name}`}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left">
          {s.completed
            ? <CheckCircle2 size={18} style={{ color: "#10B981" }} />
            : <Circle size={18} style={{ color: "#9CA3AF" }} />}
          <span className={`text-sm ${s.completed ? "text-white" : "text-[#cbd5e1]"}`}>{s.name}</span>
        </div>
      ) : (
        <button key={s.name} data-testid={`site-${task.id}-${s.name}`} onClick={() => onToggle(s)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-white/5 text-left">
          {s.completed
            ? <CheckCircle2 size={18} style={{ color: "#10B981" }} />
            : <Circle size={18} style={{ color: "#9CA3AF" }} />}
          <span className={`text-sm ${s.completed ? "text-white" : "text-[#cbd5e1]"}`}>{s.name}</span>
        </button>
      ))}
    </div>
  );
}

function TaskHeader({ task, open, onToggle }) {
  const doneCount = (task.sites || []).filter((s) => s.completed).length;
  return (
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
      <span className="flex items-center gap-2.5 min-w-0">
        {task.complete
          ? <CheckCircle2 size={18} style={{ color: "#10B981" }} data-testid={`task-complete-${task.id}`} />
          : <Circle size={18} style={{ color: "#F97316" }} data-testid={`task-incomplete-${task.id}`} />}
        <span className="text-white font-medium truncate">{task.title}</span>
      </span>
      <span className="flex items-center gap-3 flex-shrink-0">
        <span className="text-xs text-[#9CA3AF]">{doneCount}/{(task.sites || []).length} sites</span>
        <ChevronDown size={16} className={`text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`} />
      </span>
    </button>
  );
}

function TaskItem({ stageId, task, onChanged, readOnly }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {site, next}

  const toggleSite = (site) => setConfirm({ site, next: !site.completed });
  const doConfirm = async () => {
    const { site, next } = confirm;
    setConfirm(null);
    try {
      await api.put(`/implementation/stages/${stageId}/tasks/${task.id}/site`, { name: site.name, completed: next });
      onChanged();
    } catch { toast.error("Could not update task"); }
  };

  return (
    <div data-testid={`manager-task-${task.id}`} className="rounded-xl border overflow-hidden" style={{ backgroundColor: "#0A1F44", borderColor: "#1E3A8A" }}>
      <TaskHeader task={task} open={open} onToggle={() => setOpen((o) => !o)} />

      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: "#1E3A8A" }}>
          {task.instructions && <p className="text-sm text-[#cbd5e1] my-3 whitespace-pre-wrap">{task.instructions}</p>}
          <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2 mt-3">Sites</p>
          <SiteList task={task} onToggle={toggleSite} readOnly={readOnly} />
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        message={confirm ? confirmMessage(confirm) : ""}
        confirmLabel={confirm && confirm.next ? "Confirm Completion" : "Confirm"}
        onConfirm={doConfirm}
        onCancel={() => setConfirm(null)}
        testId="site-confirm"
      />
    </div>
  );
}

function stageTabColor(s, isActive) {
  if (s.locked) return "#64748B";
  if (isActive) return "#F97316";
  return STAGE_STATUS_COLOR[s.status] || "#9CA3AF";
}

function StageNav({ stages, activeId, onSelect }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 mb-6 border-b" style={{ borderColor: "#1E3A8A" }}>
      {stages.map((s) => {
        const isActive = s.id === activeId;
        const color = stageTabColor(s, isActive);
        return (
          <button key={s.id} data-testid={`stage-tab-${s.id}`} onClick={() => onSelect(s.id)}
            className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border-b-2 flex items-center gap-2"
            style={{ color, borderColor: isActive ? "#F97316" : "transparent", opacity: s.locked ? 0.65 : 1 }}>
            {s.locked
              ? <Lock size={12} data-testid={`stage-tab-lock-${s.id}`} />
              : <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} data-testid={`stage-tab-dot-${s.id}`} />}
            {s.title}
          </button>
        );
      })}
    </div>
  );
}

function LockedStagePanel({ stage }) {
  return (
    <div data-testid={`impl-locked-${stage.id}`} className="rounded-xl border p-12 text-center" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <Lock size={36} className="mx-auto mb-4" style={{ color: "#64748B" }} />
      <p className="text-white font-heading font-semibold text-lg mb-1">This stage is locked</p>
      <p className="text-[#9CA3AF] text-sm">Complete all tasks in the previous stage to unlock it.</p>
    </div>
  );
}

function StageResources({ stage, onView, onWatch }) {
  const resources = stage.resources || [];
  const videos = stage.videos || [];
  if (resources.length === 0 && videos.length === 0) return null;
  const btnClass = "flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-white border transition-colors hover:border-[#F97316]";
  const btnStyle = { borderColor: "#1E3A8A", backgroundColor: "#0A1F44" };
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">Resources</p>
      <div className="flex flex-wrap gap-2">
        {resources.map((r) => (
          isPpt(r.filename) ? (
            <button key={r.id} data-testid={`manager-resource-${r.id}`} onClick={() => downloadResource(r.file_id, r.filename)}
              className={btnClass} style={btnStyle} title="Download to view">
              <Download size={14} style={{ color: "#F97316" }} /> {r.display_name || r.filename}
            </button>
          ) : (
            <button key={r.id} data-testid={`manager-resource-${r.id}`} onClick={() => onView({ file: r, title: r.display_name || r.filename })}
              className={btnClass} style={btnStyle} title="View & download">
              <Eye size={14} style={{ color: "#F97316" }} /> {r.display_name || r.filename}
            </button>
          )
        ))}
        {videos.map((v) => (
          <button key={v.id} data-testid={`manager-video-${v.id}`} onClick={() => onWatch({ url: v.url, title: v.display_name })}
            className={btnClass} style={btnStyle}>
            <Youtube size={14} style={{ color: "#F97316" }} /> {v.display_name}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-2">Files open in the viewer where you can also download them. PowerPoint files are download-only.</p>
    </div>
  );
}

function StageContent({ stage, load, readOnly, onView, onWatch }) {
  return (
    <div className="space-y-6" data-testid={`impl-stage-${stage.id}`}>
      {stage.introduction && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
          <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">Introduction</p>
          <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{stage.introduction}</p>
        </div>
      )}

      <StageResources stage={stage} onView={onView} onWatch={onWatch} />

      <div>
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">Task List</p>
        {(stage.tasks || []).length === 0 ? (
          <div className="rounded-xl border p-8 text-center text-[#9CA3AF]" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
            <FileText size={28} className="mx-auto mb-2 opacity-40" /> No tasks for this stage yet.
          </div>
        ) : (
          <div className="space-y-3">
            {stage.tasks.map((t) => <TaskItem key={t.id} stageId={stage.id} task={t} onChanged={load} readOnly={readOnly} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function StagePanel({ stage, load, readOnly, onView, onWatch }) {
  if (!stage) return null;
  if (stage.locked) return <LockedStagePanel stage={stage} />;
  return <StageContent stage={stage} load={load} readOnly={readOnly} onView={onView} onWatch={onWatch} />;
}

function ImplEmpty() {
  return (
    <div className="text-center py-20 text-[#9CA3AF]" data-testid="impl-empty">
      <Layers size={40} className="mx-auto mb-3 opacity-40" />
      No implementation stages have been set up yet.
    </div>
  );
}

export default function ManagerImplementation({ clientId, readOnly = false }) {
  const [stages, setStages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [watch, setWatch] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/clients/${clientId}/implementation/stages`);
    setStages(data);
    setActiveId((cur) => (cur && data.some((s) => s.id === cur)) ? cur : (data[0]?.id || null));
  }, [clientId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // live visibility across managers/sites
    return () => clearInterval(t);
  }, [load]);

  const stage = stages.find((s) => s.id === activeId);

  if (stages.length === 0) return <ImplEmpty />;

  return (
    <div>
      <h1 className="font-heading text-white text-3xl font-bold mb-5">Implementation</h1>

      <StageNav stages={stages} activeId={activeId} onSelect={setActiveId} />

      <StagePanel stage={stage} load={load} readOnly={readOnly} onView={setViewing} onWatch={setWatch} />

      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload onClose={() => setViewing(null)} />}
      {watch && <VideoModal url={watch.url} title={watch.title} onClose={() => setWatch(null)} />}
    </div>
  );
}
